import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';

/**
 * Mini-weergave van de PHS-toon: een verticaal-gesplitste driehoek met het
 * tooncijfer 1-6. Style hangt af van {@link HdPlanetGridColorConfig.beneathTheLineStyle}:
 *  - `'classic'` → driehoek met border;
 *  - andere → driehoek zonder border (modern look).
 *
 * Toon-regels (verticale split):
 *  - 1-3 → links = side-color, rechts = primary;
 *  - 4-6 → links = primary, rechts = side-color.
 *
 * Geëxtraheerd uit {@link HdColorToneBlockComponent} (zie sibling
 * {@link HdColorShapeComponent}).
 */
@Component({
  selector: 'hd-tone-shape',
  templateUrl: './hd-tone-shape.component.html',
  styleUrl: './hd-tone-shape.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdToneShapeComponent {
  readonly toneNumber = input.required<number>();
  readonly side = input.required<HdBodygraphSide>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();

  protected readonly isClassicStyle = computed(() => this.colorConfig().beneathTheLineStyle === 'classic');

  protected readonly sideColor = computed(() => {
    const shape = this.colorConfig().colorToneShape;
    return this.side() === 'design' ? shape.design : shape.personality;
  });

  protected readonly primaryColor = computed(() => this.colorConfig().colorToneShape.primary);

  protected readonly leftFill = computed(() =>
    this.isLowerHalf(this.toneNumber()) ? this.sideColor() : this.primaryColor(),
  );
  protected readonly rightFill = computed(() =>
    this.isLowerHalf(this.toneNumber()) ? this.primaryColor() : this.sideColor(),
  );

  protected readonly numberColor = computed(() => {
    const shape = this.colorConfig().colorToneShape;
    return this.isClassicStyle() ? shape.classicNumberColor : shape.numberColor;
  });

  protected readonly numberTextShadow = computed(() => {
    if (!this.isClassicStyle()) return 'none';
    const outline = this.colorConfig().colorToneShape.classicNumberOutlineColor;
    return `-1px -1px 0 ${outline}, 1px -1px 0 ${outline}, -1px 1px 0 ${outline}, 1px 1px 0 ${outline}`;
  });

  protected readonly borderColor = computed(() =>
    this.isClassicStyle() ? this.colorConfig().colorToneShape.borderColor : 'none',
  );

  private isLowerHalf(value: number): boolean {
    return value >= 1 && value <= 3;
  }
}
